import React from 'react';
import { api } from '../services/api';

/**
 * Custom hook for managing video generation with async composer helpers
 * Provides state management, polling, and error handling for video generation
 */
export function useVideoGeneration(projectId) {
  const [predictionId, setPredictionId] = React.useState('');
  const [predictionStatus, setPredictionStatus] = React.useState('');
  const [predictionMeta, setPredictionMeta] = React.useState(null);
  const [isGenerating, setIsGenerating] = React.useState(false);
  const [isPolling, setIsPolling] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [progress, setProgress] = React.useState(0);
  const [telemetry, setTelemetry] = React.useState(null);

  const pollingIntervalRef = React.useRef(null);
  const abortControllerRef = React.useRef(null);

  /**
   * Sanitize payload for video generation
   */
  const sanitizePayload = React.useCallback((payload) => {
    if (!payload || typeof payload !== 'object') return {};
    
    const sanitized = { ...payload };
    
    // Remove sensitive information
    delete sanitized.userId;
    delete sanitized.email;
    delete sanitized.token;
    
    // Sanitize text fields
    if (sanitized.prompt) {
      sanitized.prompt = String(sanitized.prompt).replace(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, '[contact]');
    }
    
    // Ensure numeric values are valid
    if (sanitized.durationSec) {
      sanitized.durationSec = Math.max(5, Math.min(120, Number(sanitized.durationSec) || 30));
    }
    
    // Validate tier
    const validTiers = ['budget', 'low', 'medium', 'high', 'loudest'];
    if (sanitized.tier && !validTiers.includes(sanitized.tier)) {
      sanitized.tier = 'loudest';
    }
    
    return sanitized;
  }, []);

  /**
   * Start video generation
   */
  const startVideoGeneration = React.useCallback(async (payload) => {
    if (!projectId) {
      throw new Error('Project ID is required');
    }

    setIsGenerating(true);
    setError(null);
    setProgress(0);

    try {
      const sanitizedPayload = sanitizePayload(payload);
      const response = await api.composerVideoStart(projectId, sanitizedPayload);
      
      if (!response?.ok) {
        throw new Error(response?.error || 'Failed to start video generation');
      }

      const newPredictionId = response.predictionId || response.prediction_id;
      if (!newPredictionId) {
        throw new Error('No prediction ID received');
      }

      setPredictionId(newPredictionId);
      setPredictionStatus('starting');
      setPredictionMeta(response.meta || null);
      setTelemetry({
        startTime: Date.now(),
        tier: sanitizedPayload.tier,
        template: sanitizedPayload.template,
        mode: sanitizedPayload.mode
      });

      return newPredictionId;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsGenerating(false);
    }
  }, [projectId, sanitizePayload]);

  /**
   * Check video generation status
   */
  const checkStatus = React.useCallback(async (currentPredictionId) => {
    if (!projectId || !currentPredictionId) {
      throw new Error('Project ID and prediction ID are required');
    }

    try {
      const response = await api.composerVideoStatus(projectId, currentPredictionId);
      
      if (!response?.ok && response?.error) {
        throw new Error(response.error);
      }

      const status = response.status || 'unknown';
      const progressValue = response.progress || response.percentage || 0;
      
      setPredictionStatus(status);
      setProgress(progressValue);
      setPredictionMeta(response.meta || response.output || null);

      // Update telemetry
      if (response.meta) {
        setTelemetry(prev => ({
          ...prev,
          ...response.meta,
          lastUpdate: Date.now()
        }));
      }

      return {
        status,
        progress: progressValue,
        output: response.output || response.meta?.output,
        error: response.error
      };
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [projectId]);

  /**
   * Finalize video generation
   */
  const finalizeVideo = React.useCallback(async (currentPredictionId, finalizePayload = {}) => {
    if (!projectId || !currentPredictionId) {
      throw new Error('Project ID and prediction ID are required');
    }

    setIsGenerating(true);
    setError(null);

    try {
      const sanitizedPayload = sanitizePayload(finalizePayload);
      const response = await api.composerVideoFinalize(projectId, {
        predictionId: currentPredictionId,
        ...sanitizedPayload
      });

      if (!response?.ok) {
        throw new Error(response?.error || 'Failed to finalize video');
      }

      // Update telemetry with completion data
      setTelemetry(prev => ({
        ...prev,
        completionTime: Date.now(),
        duration: Date.now() - (prev?.startTime || Date.now()),
        finalStatus: 'completed'
      }));

      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsGenerating(false);
    }
  }, [projectId, sanitizePayload]);

  /**
   * Start polling for status updates
   */
  const startPolling = React.useCallback((currentPredictionId, options = {}) => {
    if (!currentPredictionId) return;

    const { interval = 3000, maxDuration = 300000 } = options;

    setPredictionId(String(currentPredictionId));
    
    // Clear existing polling
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    setIsPolling(true);
    setError(null);

    const startTime = Date.now();
    
    pollingIntervalRef.current = setInterval(async () => {
      try {
        if (abortControllerRef.current?.signal.aborted) {
          return;
        }

        // Check if we've exceeded max duration
        if (Date.now() - startTime > maxDuration) {
          throw new Error('Video generation timed out');
        }

        const status = await checkStatus(currentPredictionId);
        
        // Stop polling if completed or failed
        if (status.status === 'succeeded' || status.status === 'failed' || status.error) {
          stopPolling();
          
          if (status.status === 'succeeded') {
            // Auto-finalize on success
            try {
              await finalizeVideo(currentPredictionId);
            } catch (finalizeError) {
              setError(`Generation succeeded but finalization failed: ${finalizeError.message}`);
            }
          }
        }
      } catch (err) {
        stopPolling();
        setError(err.message);
      }
    }, interval);

    return () => stopPolling();
  }, [checkStatus, finalizeVideo]);

  /**
   * Stop polling
   */
  const stopPolling = React.useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    
    setIsPolling(false);
  }, []);

  /**
   * Reset all states
   */
  const reset = React.useCallback(() => {
    stopPolling();
    setPredictionId('');
    setPredictionStatus('');
    setPredictionMeta(null);
    setIsGenerating(false);
    setError(null);
    setProgress(0);
    setTelemetry(null);
  }, [stopPolling]);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return {
    // State
    predictionId,
    predictionStatus,
    predictionMeta,
    isGenerating,
    isPolling,
    error,
    progress,
    telemetry,
    
    // Actions
    startVideoGeneration,
    checkStatus,
    finalizeVideo,
    startPolling,
    stopPolling,
    reset,
    sanitizePayload
  };
}
